<template>
  <div class="brief__container">
    <h1 class="uk-hidden">Brief</h1>
    <ProgressIndicator
      :step="step"
      :steps-total="totalSteps - 1"
      :is-welcome="isWelcome"
    />
    <Header
      :is-welcome="isWelcome"
      :last-step="step === totalSteps"
      :first-step="step === 0"
      :is-mounted="isMounted"
      :is-mobile="isMobile"
      @show-leave="showLeave = true"
    />
    <form id="brief" class="brief__app" @submit.prevent @keyup.enter.prevent>
      <transition-group
        appear
        tag="div"
        class="brief__content"
        :name="isGoingBack ? 'fade-back' : 'fade'"
        v-on:before-enter="beforeEnterHandler"
        v-on:after-enter="addOverflow"
        v-on:before-appear="beforeAppear"
        v-on:after-appear="afterAppear"
      >
        <div
          class="brief__step-wrapper"
          v-for="(stepName, index) in stepNames"
          :key="index + 1"
          v-show="step === index + 1"
        >
          <component
            :is="stepName"
            :ref="`step-${index + 1}`"
            :is-mobile="isMobile"
            :form-data="form"
            @increment-step="incrementStepHandler"
            @step-data="stepDataHandler"
            @is-valid="isStepValid = $event"
          />
        </div>
      </transition-group>
      <StepActions
        :is-welcome="isWelcome"
        :step="step"
        :is-step-valid="isStepValid"
        :no-return-steps="noReturnSteps"
        :is-sending="isSending"
        :total-steps="totalSteps"
        @increment-step="incrementStepHandler"
        @reduce-step="reduceStepHandler"
        @send-form="sendFormHandler"
        @time-elapsed="timeElapsedUpdate"
      />
    </form>
    <transition name="fade">
      <LeaveModal v-show="showLeave" @close-leave="showLeave = !showLeave" />
    </transition>
  </div>
</template>
<script>
import '../../autoload/_uikit';
import axios from 'axios';
import Header from './components/global/Header.vue';
import Welcome from './components/steps/Welcome.vue';
import LetsGetToKnow from './components/steps/LetsGetToKnow.vue';
import ProgressIndicator from './components/global/ProgressIndicator.vue';
import StepActions from './components/global/StepActions.vue';
import LeaveModal from './components/global/LeaveModal.vue';
import WhatYouNeed from './components/steps/WhatYouNeed.vue';
import SiteJob from './components/steps/SiteJob.vue';
import NeedLogo from './components/steps/NeedLogo.vue';
import MaterialsForPage from './components/steps/MaterialsForPage.vue';
import SiteColors from './components/steps/SiteColors.vue';
import SiteStyle from './components/steps/SiteStyle.vue';
import InspirationSites from './components/steps/InspirationSites.vue';
import SiteFunctions from './components/steps/SiteFunctions.vue';
import OtherServices from './components/steps/OtherServices.vue';
import DomainInfo from './components/steps/DomainInfo.vue';
import HaveHosting from './components/steps/HaveHosting.vue';
import Deadline from './components/steps/Deadline.vue';
import Budget from './components/steps/Budget.vue';
import FindOutAboutUs from './components/steps/FindOutAboutUs.vue';
import ImportetToYou from './components/steps/ImportetToYou.vue';
import ThankYou from './components/steps/ThankYou.vue';
import Summary from './components/steps/Summary.vue';
import '../../../styles/main.scss';

if (process.env.NODE_ENV === 'development') {
  // eslint-disable-next-line global-require
  require('../../../styles/main.scss');
  window.adminUrl = '/wp-admin/admin-ajax.php';
}

export default {
  name: 'App',
  components: {
    LeaveModal,
    StepActions,
    ProgressIndicator,
    Header,
    Welcome,
    LetsGetToKnow,
    WhatYouNeed,
    SiteJob,
    NeedLogo,
    MaterialsForPage,
    SiteColors,
    SiteStyle,
    InspirationSites,
    SiteFunctions,
    OtherServices,
    DomainInfo,
    HaveHosting,
    Deadline,
    Budget,
    FindOutAboutUs,
    ImportetToYou,
    Summary,
    ThankYou,
  },
  data: () => ({
    step: 1,
    isMounted: false,
    isSending: false,
    isMobileValue: true,
    isStepValid: false,
    body: document.querySelector('body'),
    isGoingBack: false,
    form: {
      action: 'send_brief',
    },
    sendToUrl: '',
    showLeave: false,
    isDirty: false,
    stepNames: [
      'Welcome',
      'LetsGetToKnow',
      'WhatYouNeed',
      'SiteJob',
      'NeedLogo',
      'MaterialsForPage',
      'SiteColors',
      'SiteStyle',
      'InspirationSites',
      'SiteFunctions',
      'OtherServices',
      'DomainInfo',
      'HaveHosting',
      'Deadline',
      'Budget',
      'FindOutAboutUs',
      'ImportetToYou',
      'Summary',
      'ThankYou',
    ],
    timeElapsed: {
      seconds: 0,
      minutes: 0,
    },
  }),
  computed: {
    isWelcome() {
      return this.step === 1;
    },
    isMobile() {
      return this.isMobileValue;
    },
    totalSteps() {
      return this.stepNames.length;
    },
    noReturnSteps() {
      return [2, this.totalSteps];
    },
  },
  watch: {
    step() {
      this.updateHtmlScrollbarGutter();
    },
  },
  methods: {
    incrementStepHandler() {
      this.isGoingBack = false;
      if (this.step === 1) {
        this.removeOverflow();
      }
      this.step += 1;
    },
    reduceStepHandler() {
      this.isGoingBack = true;
      this.step -= 1;
    },
    async fetchNonce() {
      try {
        const { data } = await axios.get(this.sendToUrl, {
          params: { action: 'wp_create_nonce_brief' },
        });
        if (data && data.nonce) {
          window.briefNonce = data.nonce;
        }
      } catch (error) {
        console.warn('Could not fetch nonce from WordPress.', error);
      }
    },
    async sendFormHandler() {
      if (this.isSending) return;
      this.isSending = true;

      if (!window.briefNonce) {
        await this.fetchNonce();
      }

      if (this.form.logoImg && this.form.logoImg.length > 0) {
        const maxSize = 5 * 1024 * 1024;
        const allowedTypes = ['image/jpeg', 'image/png', 'image/svg+xml'];

        for (const file of this.form.logoImg) {
          if (file.size > maxSize) {
            UIkit.notification({
              message: `Plik ${file.name} jest za duży (max 5MB)`,
              status: 'danger',
              pos: 'bottom-right',
              timeout: 5000,
            });
            this.isSending = false;
            return;
          }

          if (!allowedTypes.includes(file.type)) {
            UIkit.notification({
              message: `Plik ${file.name} ma nieprawidłowy format (dozwolone: JPG, PNG, SVG)`,
              status: 'danger',
              pos: 'bottom-right',
              timeout: 5000,
            });
            this.isSending = false;
            return;
          }
        }
      }

      const dataToSend = new FormData();
      const requestConfig = {
        method: 'post',
        url: this.sendToUrl,
        data: dataToSend,
        headers: { 'Content-Type': 'multipart/form-data' },
      };

      dataToSend.append('_wpnonce', window.briefNonce || '');

      Object.entries(this.form).forEach(([key, value]) => {
        if (key === 'inspirationsSitesLinks') {
          dataToSend.append(
            'inspirationsSitesLinks',
            JSON.stringify(this.form.inspirationsSitesLinks),
          );
          dataToSend.append(
            'timeElapsedMinutes',
            this.timeElapsed.minutes,
          );
          dataToSend.append(
            'timeElapsedSeconds',
            this.timeElapsed.seconds,
          );
        } else if (key === 'logoImg') {
          this.form.logoImg.forEach((item, index) => {
            dataToSend.append('logos[]', this.form.logoImg[index], item.name);
          });
        } else {
          dataToSend.append(key, value);
        }
      });

      try {
        const response = await axios(requestConfig);
        if (!response.data || response.data.success !== true) {
          throw new Error('Brief was not saved');
        }
        this.step += 1;
      } catch (error) {
        console.error(error);
        UIkit.notification({
          message: 'Coś poszło nie tak, spróbuj ponownie za chwilę',
          status: 'danger',
          pos: 'bottom-right',
          timeout: 8000,
        });
        this.isSending = false;
      }
    },
    beforeEnterHandler() {
      this.removeOverflow();
      if (!this.isWelcome && this.step !== this.totalSteps) {
        this.$refs[`step-${this.step}`][0].emitStep();
      }

      // fire fireworks
      if (this.step === this.totalSteps) {
        this.$refs[`step-${this.step}`][0].fireworks();
      }
    },
    toggleOverflow() {
      this.body.classList.toggle('page-change');
    },
    removeOverflow() {
      this.body.classList.add('page-change');
      document.documentElement.classList.add('page-change');
    },
    addOverflow() {
      this.body.classList.remove('page-change');
      document.documentElement.classList.remove('page-change');
    },
    beforeAppear() {
      this.removeOverflow();
    },
    afterAppear() {
      if (!this.isWelcome) {
        this.addOverflow();
      }
    },
    isMobileHandler() {
      this.isMobileValue = window.innerWidth < 640;
    },
    stepDataHandler(payload) {
      this.form = { ...this.form, ...payload };
      this.isDirty = true;
    },
    leaveAppActionsHandler(e) {
      if (this.step === this.totalSteps || this.showLeave || this.step === 0) {
        return;
      }

      switch (performance.navigation.type) {
        case 0:
          console.info('TYPE_NAVIGATE');
          e.preventDefault();
          e.returnValue = '';
          break;
        case 1:
          console.info('TYPE_RELOAD');
          e.preventDefault();
          e.returnValue = '';
          break;
        case 2:
          console.info('TYPE_BACK_FORWARD');
          e.preventDefault();
          e.returnValue = '';
          break;
        default:
          console.log(e);
          console.log(performance.navigation.type);
      }
    },
    timeElapsedUpdate(timeElapsed) {
      this.timeElapsed = timeElapsed;
    },
    updateHtmlScrollbarGutter() {
      const { documentElement } = document;

      documentElement.classList.toggle(
        'brief-no-scrollbar-gutter',
        this.step === this.totalSteps,
      );
      documentElement.classList.toggle(
        'brief-no-scrollbar',
        this.isWelcome,
      );
    },
  },
  mounted() {
    this.sendToUrl = window.adminUrl;
    this.isMounted = true;
    this.isMobileHandler();
    this.updateHtmlScrollbarGutter();

    this.handleBeforeUnload = (e) => {
      if (this.isDirty) {
        this.leaveAppActionsHandler(e);
      }
    };

    this.handleResize = () => {
      this.isMobileHandler();
    };

    window.addEventListener('beforeunload', this.handleBeforeUnload);
    window.addEventListener('resize', this.handleResize);
  },
  beforeDestroy() {
    window.removeEventListener('beforeunload', this.handleBeforeUnload);
    window.removeEventListener('resize', this.handleResize);
    document.documentElement.classList.remove(
      'brief-no-scrollbar-gutter',
      'brief-no-scrollbar',
      'page-change',
    );
  },
};
</script>
<style lang="scss">
/* prettier-ignore */
@import "scss/main";

.brief {
  &__content {
    min-height: 100vh;
    flex-grow: 1;
    display: flex;
    flex-direction: column;
  }

  &__step-wrapper {
    display: flex;
    flex-grow: 1;
  }
}

.fade-enter-active,
.fade-leave-active,
.fade-back-enter-active,
.fade-back-leave-active {
  transition: all 0.8s;
  min-height: 100vh;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
}

.fade-enter {
  opacity: 0;
  transform: translateY(rem(1000px));
}

.fade-leave-to {
  opacity: 0;
  transform: translateY(rem(-1000px));
}

.fade-back-enter {
  opacity: 0;
  transform: translateY(rem(-1000px));
}

.fade-back-leave-to {
  opacity: 0;
  transform: translateY(rem(1000px));
}

.fade-input-enter-active,
.fade-input-leave-active {
  transition: all 0.4s;
}

.fade-input-enter {
  opacity: 0;
  transform: translateY(rem(20px));
}

.fade-input-leave-to {
  opacity: 0;
  transform: translateY(rem(-20px));
}

.opacity-enter-active,
.opacity-leave-active {
  transition: $t-opacity;
}

.opacity-enter {
  opacity: 0;
}

.opacity-leave-to {
  opacity: 0;
}

.circle-check::after,
.circle-check::before {
  border-radius: 100% !important;
}
</style>
